/**
 * SecondSightPassiveSkillEffect — Astral oracle-path passive: predicted-dodge
 * damage bonus.
 *
 * Second Sight watches for "predicted dodges" — situations where the player
 * was inside an enemy's Foresight arc zone at attack-start time, then moved
 * clear before the attack resolved. When that pattern fires, the player's
 * next Astral skill hit within 4 seconds deals 25% bonus damage (×1.25
 * multiplier). The window is one-shot: a single hit consumes it, whether or
 * not the 4-second timer has elapsed.
 *
 * Integration points:
 *   1. `onEnemyWindup(entityId, playerIsInZone)` — call this at the same
 *      moment that ForesightPassiveSkillEffect.onEnemyWindup() fires. Pass
 *      `true` if the player is currently inside the attack arc projection.
 *      The caller is responsible for computing the zone check.
 *
 *   2. `onAttackResolved(entityId, hitPlayer)` — call this when the enemy's
 *      attack fires. If `hitPlayer` is false AND the player was recorded as
 *      "in zone" at windup time, this counts as a predicted dodge and the
 *      Second Sight bonus window opens.
 *
 *   3. `consumeBonusIfActive()` — called by the combat system when an Astral
 *      skill lands a hit. Returns 1.25 and clears the window on the first
 *      call while active; returns 1.0 otherwise.
 *
 * Precision: only position-based dodges trigger the bonus. An attack that
 * misses for some other reason (AI interrupt, range abort) does not fire
 * Second Sight — the player must have been in the arc zone at windup and
 * moved out before resolution.
 *
 * Effect keys (from tree JSON):
 *   - `dodgeBonusDamage`: 0.25 — multiplier added on top of base (result is ×1.25)
 *   - `windowMs`: 4000 — how long the bonus window stays open
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § oracle_path_passive
 * @see design-decisions.md § Second Sight Dodge Definition: Positional
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SecondSightConfig {
  /** Additive damage bonus on predicted-dodge window (0.25 → ×1.25 multiplier). */
  readonly dodgeBonusDamage: number;
  /** How long the bonus window stays open in ms (4000ms). */
  readonly windowMs: number;
}

const DEFAULT_CONFIG: SecondSightConfig = {
  dodgeBonusDamage: 0.25,
  windowMs: 4000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SecondSightPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'oracle_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  readonly config: SecondSightConfig;

  /**
   * Per-enemy zone-at-windup state.
   * Key: entityId. Value: true if the player was in the arc zone at windup time.
   * Cleared when the corresponding attack resolves.
   */
  private readonly _inZoneAtWindup = new Map<string, boolean>();

  /** True while the 25% bonus window is open. */
  private _windowActive = false;
  /** Remaining ms of the open bonus window. Counts down while window is active. */
  private _windowRemaining = 0;
  /** Total number of predicted dodges detected since construction. */
  private _dodgesDetected = 0;

  constructor(config: Partial<SecondSightConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get isWindowActive(): boolean { return this._windowActive; }
  get windowRemaining(): number { return this._windowRemaining; }
  get dodgesDetected(): number { return this._dodgesDetected; }

  // ---------------------------------------------------------------------------
  // Integration API — called by external systems
  // ---------------------------------------------------------------------------

  /**
   * Called at the same moment that ForesightPassiveSkillEffect.onEnemyWindup()
   * fires, with a precomputed zone-membership flag.
   *
   * @param entityId      Unique identifier of the attacking enemy.
   * @param playerIsInZone  Whether the player is inside the attack arc at
   *                        wind-up time.
   */
  onEnemyWindup(entityId: string, playerIsInZone: boolean): void {
    this._inZoneAtWindup.set(entityId, playerIsInZone);
  }

  /**
   * Called when the enemy's attack actually fires.
   *
   * A predicted dodge is detected when:
   *   1. The player was recorded as "in zone" at windup time.
   *   2. The attack did NOT hit the player.
   *
   * On a predicted dodge, the bonus window opens (or resets if already open).
   * The zone-at-windup record is cleared regardless of outcome.
   *
   * @param entityId   The same entityId passed to onEnemyWindup.
   * @param hitPlayer  Whether the attack actually struck the player.
   */
  onAttackResolved(entityId: string, hitPlayer: boolean): void {
    const wasInZone = this._inZoneAtWindup.get(entityId) ?? false;
    this._inZoneAtWindup.delete(entityId);

    if (!hitPlayer && wasInZone) {
      // Predicted dodge — open or reset the bonus window.
      this._windowActive = true;
      this._windowRemaining = this.config.windowMs;
      this._dodgesDetected++;
    }
  }

  /**
   * Called by the combat system when an Astral skill lands a hit.
   *
   * If the Second Sight bonus window is active, consumes it and returns the
   * full damage multiplier (1 + dodgeBonusDamage = 1.25 by default).
   * If the window is not active, returns 1.0 (no bonus).
   *
   * The window is one-shot: consuming it closes it immediately, regardless
   * of how much time remains.
   */
  consumeBonusIfActive(): number {
    if (!this._windowActive) return 1.0;
    this._windowActive = false;
    this._windowRemaining = 0;
    return 1.0 + this.config.dodgeBonusDamage;
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  /** Decay the open bonus window. Clears automatically on expiry. */
  update(delta: number): void {
    if (!this._windowActive) return;
    this._windowRemaining = Math.max(0, this._windowRemaining - delta);
    if (this._windowRemaining === 0) {
      this._windowActive = false;
    }
  }

  /** Passives do not respond to player activation. */
  activate(): boolean { return false; }

  /** Window active = skill is "active" in the sense that a bonus is waiting. */
  isActive(): boolean { return this._windowActive; }

  destroy(): void {
    this._inZoneAtWindup.clear();
    this._windowActive = false;
    this._windowRemaining = 0;
  }
}
